import MirrorBackButton from "@/components/MirrorBackButton";
import React, { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, MotionConfig, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import {
  Plus, CheckCircle2, Circle, ChevronLeft, Flame,
  Sparkles, Pencil, Trash2, X, Check, ArrowRight, Loader2, Mic, Square,
} from "lucide-react";
import MobileLayout from "@/components/MobileLayout";
import { useLanguage } from "@/i18n/LanguageProvider";
import { InfoTooltip } from "@/components/InfoTooltip";
import { useHabits, newHabitId, snapshotForHabit, reviewToday, type HabitColor, type HabitFrequency, type HabitSnapshot } from "@/hooks/useHabits";
import { HabitPendingActionLauncher } from "@/components/mirror/HabitActionModal";
import { useXP } from "@/hooks/useXP";
import { SourceBadge } from "@/components/SourceBadge";
import { sfx } from "@/lib/sfx";
import { useVoiceInputWhisper } from "@/hooks/useVoiceInputWhisper";

// ── Color system ────────────────────────────────────────────────────────────

const COLOR_MAP: Record<HabitColor, { bg: string; border: string; text: string; glow: string }> = {
  cyan:    { bg: "hsl(187 72% 50% / 0.10)", border: "hsl(187 72% 50% / 0.35)", text: "hsl(187 72% 60%)", glow: "hsl(187 72% 50% / 0.25)" },
  amber:   { bg: "hsl(45 90% 60% / 0.10)",  border: "hsl(45 90% 60% / 0.35)",  text: "hsl(45 90% 65%)",  glow: "hsl(45 90% 60% / 0.25)"  },
  rose:    { bg: "hsl(340 72% 60% / 0.10)", border: "hsl(340 72% 60% / 0.35)", text: "hsl(340 72% 65%)", glow: "hsl(340 72% 60% / 0.25)" },
  violet:  { bg: "hsl(265 72% 60% / 0.10)", border: "hsl(265 72% 60% / 0.35)", text: "hsl(265 72% 65%)", glow: "hsl(265 72% 60% / 0.25)" },
  emerald: { bg: "hsl(142 71% 45% / 0.10)", border: "hsl(142 71% 45% / 0.35)", text: "hsl(142 71% 55%)", glow: "hsl(142 71% 45% / 0.25)" },
};

// ── Emoji & form constants ──────────────────────────────────────────────────

const EMOJI_SUGGESTIONS = [
  "\u{1F3C3}", "\u{1F4AA}", "\u{1F9D8}", "\u{1F6B6}", "\u{1F3CB}\u{FE0F}", "\u{1F6B4}", "\u{1F938}", "\u{1F3CA}",
  "\u{1F4A7}", "\u{1F957}", "\u{1F34E}", "\u{1F634}", "\u{1F48A}", "\u{1F9F4}", "\u{1FAC1}", "\u{2764}\u{FE0F}",
  "\u{1F9E0}", "\u{1F4DA}", "\u{270D}\u{FE0F}", "\u{1F3AF}", "\u{1F52C}", "\u{1F4A1}", "\u{1F3B5}", "\u{1F3A8}",
  "\u{1F305}", "\u{1F319}", "\u{2615}", "\u{1F6C1}", "\u{1FAB4}", "\u{1F9F9}", "\u{1F4F1}", "\u{1F4B0}",
  "\u{1F64F}", "\u{1F4AC}", "\u{1F468}\u{200D}\u{1F469}\u{200D}\u{1F467}", "\u{1F30D}", "\u{2B50}", "\u{1F525}", "\u{2728}", "\u{1F389}",
];
const COLOR_OPTIONS: HabitColor[] = ["cyan", "amber", "rose", "violet", "emerald"];
const FREQ_OPTIONS: HabitFrequency[] = ["daily", "weekdays", "weekends", "custom"];

const HERO_BG = "https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/pictures/room%20ex_06.jpeg";
const MIRROR_INSIGHT_BG = "https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/mirror-brief/room%20ex_015.jpeg";

const HabitForm = ({
  initial,
  onSave,
  onCancel,
  saving,
  t,
  error,
  locked = false,
}: {
  initial?: { emoji: string; name: string; frequency: HabitFrequency; color: HabitColor | null; custom_days?: number[] | null };
  onSave: (data: { emoji: string; name: string; frequency: HabitFrequency; color: HabitColor | null; custom_days?: number[] | null }) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
  error?: string;
  locked?: boolean;
  t: (k: string) => string;
}) => {
  const [emoji, setEmoji] = useState(initial?.emoji ?? "\u2728");
  const [name, setName] = useState(initial?.name ?? "");
  const [frequency, setFrequency] = useState<HabitFrequency>(initial?.frequency ?? "daily");
  const [color, setColor] = useState<HabitColor | null>(initial ? initial.color : "cyan");
  const [customDays, setCustomDays] = useState<number[]>(initial?.custom_days ?? []);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const { lang } = useLanguage();
  const voice = useVoiceInputWhisper(lang, { surface: "work" });
  const mounted = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const dictate = async () => {
    if (voice.isTranscribing) return;
    if (voice.isListening) {
      const text = await voice.stopListening();
      if (mounted.current && text.trim()) setName(previous => [previous.trim(), text.trim()].filter(Boolean).join(" "));
    } else await voice.startListening();
  };
  const selected = COLOR_MAP[color ?? "cyan"] ?? COLOR_MAP.cyan;
  const reducedMotion = useReducedMotion();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.35 }}
      className="space-y-5"
    >
      {error && <p role="alert" className="text-sm text-amber-300">{error}</p>}
      <fieldset disabled={saving || locked} className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-[18px] font-semibold text-foreground">
          {initial ? t("habits.form.editTitle") : t("habits.form.addTitle")}
        </h2>
        <button onClick={onCancel} aria-label={t("common.close")}
          className="w-8 h-8 rounded-xl bg-card/50 border border-border/20 flex items-center justify-center">
          <X size={14} className="text-muted-foreground" />
        </button>
      </div>

      {/* Icon/Emoji picker */}
      <div>
        <p className="text-[10px] font-display tracking-[0.18em] uppercase mb-2"
          style={{ color: "hsl(218 14% 55%)" }}>
          {t("habits.form.icon")}
        </p>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowEmojiPicker(!showEmojiPicker)} aria-label={t("habits.form.icon")} aria-expanded={showEmojiPicker}
            className="w-14 h-14 rounded-2xl flex items-center justify-center text-[28px] transition-all duration-200"
            style={{ background: selected.bg, border: `1px solid ${selected.border}` }}
          >
            {emoji}
          </button>
        </div>
        <AnimatePresence>
          {showEmojiPicker && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="mt-3 p-3 rounded-2xl"
              style={{ background: "hsl(220 35% 12%)", border: "1px solid hsl(var(--border) / 0.25)" }}
            >
              <div className="grid grid-cols-6 sm:grid-cols-8 gap-1.5 max-h-[120px] overflow-y-auto">
                {EMOJI_SUGGESTIONS.map(e => (
                  <button key={e}
                    onClick={() => { setEmoji(e); setShowEmojiPicker(false); }}
                    className="text-[20px] p-1.5 rounded-lg hover:bg-white/6 transition-colors flex items-center justify-center">
                    {e}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Name */}
      <div>
        <p className="text-[10px] font-display tracking-[0.18em] uppercase mb-2"
          style={{ color: "hsl(218 14% 55%)" }}>
          {t("habits.form.name")}
        </p>
        <input
          type="text" aria-label={t("habits.form.name")} autoFocus
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder={t("habits.form.namePlaceholder")}
          className="w-full rounded-xl px-4 py-3 text-[14px] font-display focus:border-primary/30 transition-all outline-none"
          style={{ background: "hsl(220 35% 12%)", border: "1px solid hsl(var(--border) / 0.30)", color: "hsl(210 20% 93%)" }}
        />
      </div>

      {voice.supported && <button type="button" onClick={() => void dictate()} disabled={voice.isTranscribing} aria-pressed={voice.isListening} className="min-h-11 flex items-center gap-2 text-sm text-primary">
        {voice.isListening ? <Square size={16} /> : <Mic size={16} />}
        {t(voice.isTranscribing ? "agenda.voice.transcribing" : voice.isListening ? "ux.notes.stopDictation" : "ux.notes.dictate")}
      </button>}

      {/* Color */}
      <div>
        <p className="text-[10px] font-display tracking-[0.18em] uppercase mb-2"
          style={{ color: "hsl(218 14% 55%)" }}>
          {t("habits.form.color")}
        </p>
        <div className="flex gap-2">
          {COLOR_OPTIONS.map(c => (
            <button key={c}
              onClick={() => setColor(c)} aria-label={t(`habits.color.${c}`)} aria-pressed={color === c}
              className="w-9 h-9 rounded-xl transition-all duration-200"
              style={{
                background: COLOR_MAP[c].bg,
                border: color === c ? `2px solid ${COLOR_MAP[c].text}` : `1px solid ${COLOR_MAP[c].border}`,
                transform: color === c ? "scale(1.1)" : "scale(1)",
              }}
            />
          ))}
        </div>
      </div>

      {/* Frequency */}
      <div className="space-y-3">
        <p className="text-[10px] font-display tracking-[0.18em] uppercase"
          style={{ color: "hsl(218 14% 55%)" }}>
          {t("habits.form.frequency")}
        </p>
        <div className="grid grid-cols-2 gap-2">
          {FREQ_OPTIONS.map(f => (
            <button key={f}
              aria-pressed={frequency === f} onClick={() => { setFrequency(f); if (f !== "custom") setCustomDays([]); }}
              className="py-2.5 rounded-xl text-[11px] font-display font-medium transition-all duration-200"
              style={{
                background: frequency === f ? selected.bg : "hsl(220 35% 12%)",
                border: frequency === f ? `1px solid ${selected.border}` : "1px solid hsl(var(--border) / 0.25)",
                color: frequency === f ? selected.text : "hsl(218 14% 60%)",
              }}
            >
              {t(`habits.freq.${f}`)}
            </button>
          ))}
        </div>

        <AnimatePresence>
          {frequency === "custom" && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="space-y-2"
            >
              <p className="text-[10px] font-display tracking-[0.18em] uppercase"
                style={{ color: "hsl(218 14% 55%)" }}>
                {t("habits.form.chooseDays")}
              </p>
              <div className="flex gap-1.5">
                {[0, 1, 2, 3, 4, 5, 6].map((day) => {
                  const isSelected = customDays.includes(day);
                  return (
                    <button
                      key={day} aria-pressed={isSelected}
                      onClick={() =>
                        setCustomDays(prev =>
                          isSelected ? prev.filter(d => d !== day) : [...prev, day]
                        )
                      }
                      className="flex-1 py-2 rounded-xl text-[11px] font-display font-semibold transition-all duration-200"
                      style={{
                        background: isSelected ? selected.bg : "hsl(220 35% 12%)",
                        border: isSelected ? `1px solid ${selected.border}` : "1px solid hsl(var(--border) / 0.22)",
                        color: isSelected ? selected.text : "hsl(218 14% 60%)",
                      }}
                    >
                      {t(`habits.day.${day}`)}
                    </button>
                  );
                })}
              </div>
              {customDays.length === 0 && (
                <p className="text-[10px] font-body text-center" style={{ color: "hsl(218 14% 55%)" }}>
                  {t("habits.form.selectAtLeastOne")}
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Save button with shimmer */}
      <button
        onClick={() => {
          if (!name.trim()) return;
          if (frequency === "custom" && customDays.length === 0) return;
          onSave({ emoji, name: name.trim(), frequency, color, custom_days: frequency === "custom" ? customDays : null });
        }}
        disabled={saving || voice.isListening || voice.isTranscribing || !name.trim() || (frequency === "custom" && customDays.length === 0)}
        className="w-full h-14 rounded-2xl font-display text-[14px] font-semibold tracking-wide transition-all duration-200 disabled:opacity-40 active:scale-[0.98] flex items-center justify-center gap-2 relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${selected.bg}, ${selected.glow})`, border: `1px solid ${selected.border}`, color: selected.text }}
      >
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.06) 50%, transparent 100%)", animation: reducedMotion ? "none" : "shimmer 2.5s ease-in-out infinite" }} />
        <Check size={16} />
        {saving ? t("habits.form.saving") : t("habits.form.save")}
      </button>
      </fieldset>
    </motion.div>
  );
};

// ── Main component ──────────────────────────────────────────────────────────

const Habits = () => {
  const state = useHabits(); const { t } = useLanguage(); const navigate = useNavigate();
  if (!state.hasData) return <MobileLayout className="p-6 pt-16">
    <h1 className="text-2xl mb-4">{t("habits.title")}</h1>
    {state.status === "signed-out" ? <button onClick={() => navigate("/auth?mode=signin", { state: { from: "/habits" } })}>{t("habits.signIn")}</button>
      : state.status === "error" ? <div role="alert"><p>{t("habits.loadError")}</p><button className="min-h-11 underline" disabled={state.isRefreshing} onClick={() => void state.refresh()}>{t("common.retry")}</button></div>
      : <p role="status">{t("common.loading")}</p>}
  </MobileLayout>;
  return <MotionConfig reducedMotion="user"><HabitsContent key={state.userId + ":" + state.identityGeneration} state={state} /></MotionConfig>;
};
const HabitsContent = ({ state }: { state: ReturnType<typeof useHabits> }) => {
  const navigate = useNavigate();
  const { t, tf, lang } = useLanguage();
  const { habits, loading, saving, todayDone, todayTotal, historyComplete, bestStreak,
          toggleToday, addHabit, updateHabit, deleteHabit, weekData, status, refresh, isRefreshing, capture } = state;
  const { totalXP, level, getLevelProgress } = useXP({ guarded: true });

  const reducedMotion = useReducedMotion();
  const busy = useRef(false); const [draftId, setDraftId] = useState(newHabitId);
  const [formError, setFormError] = useState<string>();
  const [reviewChanged, setReviewChanged] = useState(false);
  const [editingSnapshot, setEditingSnapshot] = useState<HabitSnapshot | null>(null);
  const [deleteSnapshot, setDeleteSnapshot] = useState<HabitSnapshot | null>(null);
  const mutationError = (cause: unknown) => { const changed = /changed|stale|snapshot|conflict/.test(cause instanceof Error ? cause.message : String(cause)); setReviewChanged(changed); setFormError(t(changed ? "habitAction.changed" : "habits.saveError")); };
  const refreshReview = async () => { const current = capture(); try { const result = await refresh(); if (current() && result) { setView("list"); setEditingHabit(null); setEditingSnapshot(null); setDeleteSnapshot(null); setConfirmDeleteId(null); setReviewChanged(false); setFormError(undefined); } } catch { if (current()) setFormError(t("habits.loadError")); } };
  const openAdd = () => { setDraftId(newHabitId()); setFormError(undefined); setReviewChanged(false); setView("add"); };
  const [view, setView] = useState<"list" | "add" | "edit">("list");
  const [editingHabit, setEditingHabit] = useState<typeof habits[0] | null>(null);
  const [justCompleted, setJustCompleted] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const displayedReview = reviewToday();
  const progress = todayTotal > 0 ? (todayDone / todayTotal) * 100 : 0;
  const habitScore = Math.round(progress);

  const orderedHabits = useMemo(() => [...habits].sort((a, b) =>
    (a.doneToday ? 1 : a.dueToday ? 0 : 2) - (b.doneToday ? 1 : b.dueToday ? 0 : 2)), [habits]);
  useEffect(() => {
    if (!justCompleted) return;
    const timer = setTimeout(() => setJustCompleted(null), 1200);
    return () => clearTimeout(timer);
  }, [justCompleted]);
  const handleToggle = async (habit: typeof habits[0]) => {
    const id = habit.id, snapshot = snapshotForHabit(habit), review = displayedReview;
    if (!snapshot) { toast.error(t("habitAction.readOnly")); return; }
    if (busy.current) return;
    const current = capture(); busy.current = true;
    try {
      const result = await toggleToday(id, snapshot, review); if (!current() || !result.completed) return;
      setJustCompleted(id); sfx.success("module");
      for (const award of result.xpAwards ?? []) {
        if (!current()) return;
        if (award.inserted) toast.success(tf("habits.xp.earned", { amount: String(award.amount) }));
      }
      if (current() && result.xpResult?.leveledUp && result.xpAwards?.some(award => award.inserted)) toast.success(t("habits.xp.levelUp") + " " + result.xpResult.newLevel);
    } catch (error) { if (current()) toast.error(t(/changed|stale|snapshot|conflict/.test(error instanceof Error ? error.message : String(error)) ? "habitAction.changed" : "habits.saveError")); }
    finally { if (current()) busy.current = false; }
  };

  const handleAdd = async (data: Parameters<typeof addHabit>[0]) => {
    if (busy.current) return;
    const current = capture(); busy.current = true; setFormError(undefined);
    try { await addHabit(data, { id: draftId }); if (current()) { toast.success(t("habits.added")); setView("list"); } }
    catch (cause) { if (current()) mutationError(cause); }
    finally { if (current()) busy.current = false; }
  };
  const handleUpdate = async (data: Parameters<typeof addHabit>[0]) => {
    if (!editingHabit || !editingSnapshot || reviewChanged || busy.current) return;
    const current = capture(); busy.current = true; setFormError(undefined);
    try { await updateHabit(editingHabit.id, data, editingSnapshot); if (current()) { toast.success(t("habits.updated")); setView("list"); setEditingHabit(null); } }
    catch (cause) { if (current()) mutationError(cause); }
    finally { if (current()) busy.current = false; }
  };
  const handleDelete = async (id: string) => {
    if (!deleteSnapshot || reviewChanged || busy.current) return;
    const current = capture(); busy.current = true; setFormError(undefined);
    try { await deleteHabit(id, deleteSnapshot); if (current()) { setView("list"); setEditingHabit(null); setConfirmDeleteId(null); toast.success(t("habits.deleted")); } }
    catch (cause) { if (current()) mutationError(cause); }
    finally { if (current()) busy.current = false; }
  };

  return (
    <MobileLayout key={lang} className="pb-28 pt-8 relative overflow-hidden"
      style={{ background: "linear-gradient(180deg, #0a0a0f 0%, #0d1117 100%)" }}>

      {/* Ambient background */}
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at top left, hsl(220 70% 20% / .25), transparent 55%)" }} />
      {status === "error" && <div role="alert" className="relative z-10 p-3 mb-4 rounded-xl border border-amber-300/30 text-amber-200 text-sm">
        <p>{t("habits.loadError")}</p><button disabled={isRefreshing} onClick={() => void refresh()} className="underline min-h-11">{t("common.retry")}</button>
      </div>}

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes rotatingBorder {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        @keyframes sparkle {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>

      {state.userId && <HabitPendingActionLauncher ownerId={state.userId} busy={saving} onMirrorResume={locator => navigate("/mirror", { state: { habitActionRecovery: locator } })} />}
      {reviewChanged && <button className="relative z-10 min-h-11 underline" disabled={isRefreshing} onClick={() => void refreshReview()}>{t("habitAction.refreshReview")}</button>}
      {!historyComplete && <p role="status" className="relative z-10">{t("habitAction.partial")}</p>}
      <AnimatePresence mode="wait">

        {view === "list" && (
          <motion.div
            key="list"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35 }}
            className="space-y-4 relative z-10"
          >
            <MirrorBackButton className="mb-1" />

            <div className="flex items-center justify-between">
              <div>
                <p className="text-[9px] font-display tracking-[0.2em] uppercase mb-0.5"
                  style={{ color: "hsl(187 72% 50% / 0.55)" }}>
                  {t("habits.subtitle")}
                </p>
                <div className="flex items-center gap-2">
                  <h1 className="font-display text-[28px] font-semibold text-foreground leading-none">
                    {t("habits.title")}
                  </h1>
                  <InfoTooltip title={t("tooltips.habits.title")} description={t("tooltips.habits.description")} />
                </div>
              </div>
              <button
                onClick={openAdd} aria-label={t("habits.addHabit")} disabled={saving}
                className="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 active:scale-95"
                style={{ background: "hsl(187 72% 50% / 0.10)", border: "1px solid hsl(187 72% 50% / 0.25)" }}
              >
                <Plus size={17} style={{ color: "hsl(187 72% 55%)" }} />
              </button>
            </div>

            <p className="text-xs text-muted-foreground">{displayedReview.completed_date} · {displayedReview.timezone}</p>

            {/* ── Hero section ────────────────────────────────────────── */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              className="rounded-2xl relative overflow-hidden"
              style={{
                background: `linear-gradient(rgba(0,0,0,0.15), rgba(0,0,0,0.35)), url(${HERO_BG}) center/cover no-repeat`,
                maxHeight: 140,
                border: "1px solid hsl(187 72% 50% / 0.22)",
              }}
            >
              {/* Hero star particles */}
              {[...Array(6)].map((_, i) => (
                <motion.div key={`hs${i}`} className="absolute rounded-full pointer-events-none"
                  style={{ width: 1.5, height: 1.5, background: i % 2 === 0 ? "#00e5ff" : "#fff", opacity: 0.3, left: `${15 + i * 14}%`, top: `${20 + (i % 3) * 25}%` }}
                  animate={{ opacity: [0.15, 0.5, 0.15], y: [0, -(8 + i * 3), 0] }}
                  transition={{ duration: 4 + i, repeat: reducedMotion ? 0 : Infinity, ease: "easeInOut", delay: i * 0.7 }}
                />
              ))}
              <div className="relative flex items-center gap-4 p-4">
                {/* Circular progress ring with glow */}
                <div className="relative shrink-0">
                  <div className="absolute inset-0 rounded-full" style={{ background: "radial-gradient(circle, hsl(187 72% 50% / 0.15), transparent 70%)", transform: "scale(1.4)" }} />
                  <svg width="72" height="72" viewBox="0 0 72 72">
                    <circle cx="36" cy="36" r="28" fill="none" stroke="hsl(218 14% 16%)" strokeWidth="5" />
                    <circle cx="36" cy="36" r="28" fill="none" stroke="hsl(187 72% 50%)" strokeWidth="5"
                      strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 28}
                      strokeDashoffset={2 * Math.PI * 28 * (1 - (historyComplete ? habitScore : 0) / 100)}
                      transform="rotate(-90 36 36)"
                      style={{ transition: "stroke-dashoffset 0.8s ease", filter: "drop-shadow(0 0 6px hsl(187 72% 50% / 0.5))" }}
                    />
                    <text x="36" y="40" textAnchor="middle" fill="white" fontSize="16" fontWeight="bold" fontFamily="'Sora', sans-serif">
                      {historyComplete ? todayTotal ? habitScore : "—" : "…"}
                    </text>
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display text-[15px] font-semibold text-white">
                    {historyComplete ? todayTotal ? `${todayDone} / ${todayTotal} ${t("habits.doneOf")}` : t("habits.restDay") : t("habitAction.partial")}
                  </p>
                  {habits.every(habit => habit.completions_complete) && bestStreak && (bestStreak.streak ?? 0) > 0 && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <Flame size={13} style={{ color: "hsl(25 95% 55%)" }} />
                      <p className="text-[12px] font-display font-medium" style={{ color: "hsl(25 95% 65%)" }}>
                        {bestStreak.streak} {t("habits.daysStraight")}
                      </p>
                    </div>
                  )}
                  {historyComplete && progress === 100 && (
                    <motion.p initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                      className="text-[11px] font-display mt-1" style={{ color: "hsl(142 71% 55%)" }}>
                      {t("habits.allDone")}
                    </motion.p>
                  )}
                </div>
              </div>
            </motion.div>

            {/* ── XP bar ────────────────────────────────────────────── */}
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.4 }}
              className="flex items-center gap-3 rounded-xl px-3 py-2"
              style={{ background: "hsl(220 35% 12%)", border: "1px solid hsl(187 72% 50% / 0.15)" }}
            >
              <span className="px-2 py-0.5 rounded-full font-display text-[10px] font-bold shrink-0"
                style={{ background: "hsl(187 72% 50% / 0.12)", border: "1px solid hsl(187 72% 50% / 0.30)", color: "hsl(187 72% 60%)", textShadow: "0 0 8px hsl(187 72% 50% / 0.30)" }}>
                {tf("habits.xp.level", { level: String(level) })}
              </span>
              <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "hsl(218 14% 18%)" }}>
                <motion.div
                  className="h-full rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${getLevelProgress()}%` }}
                  transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
                  style={{ background: "linear-gradient(90deg, hsl(187 72% 40%), hsl(187 72% 60%))", boxShadow: "0 0 6px hsl(187 72% 50% / 0.30)" }}
                />
              </div>
              <span className="text-[9px] font-display shrink-0" style={{ color: "hsl(218 14% 55%)" }}>
                {totalXP} XP
              </span>
            </motion.div>

            <p className="text-xs text-muted-foreground">{t("habits.checkHint")} {t("habits.sequenceHelp")}</p>
            {/* ── Habit cards ─────────────────────────────────────────── */}
            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-[76px] rounded-2xl animate-pulse" style={{ background: "hsl(220 35% 12%)" }} />
                ))}
              </div>
            ) : habits.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="text-[40px] mb-4">{"\u{1F331}"}</div>
                <p className="font-display text-[14px]" style={{ color: "hsl(218 14% 60%)" }}>{t("habits.empty")}</p>
                <p className="text-[11px] font-body mt-1" style={{ color: "hsl(218 14% 55%)" }}>{t("habits.emptySubtitle")}</p>
                <button
                  onClick={openAdd} aria-label={t("habits.addHabit")} disabled={saving}
                  className="mt-5 flex items-center gap-2 px-5 py-2.5 rounded-xl font-display text-[12px] font-semibold transition-all active:scale-95"
                  style={{ background: "hsl(187 72% 50% / 0.10)", border: "1px solid hsl(187 72% 50% / 0.28)", color: "hsl(187 72% 55%)" }}
                >
                  <Plus size={13} />
                  {t("habits.addFirst")}
                </button>
              </div>
            ) : (
              <div className="space-y-2.5">
                {orderedHabits.map((habit, i) => {
                  const colors = COLOR_MAP[habit.color ?? "cyan"] ?? COLOR_MAP.cyan;
                  const isJustDone = justCompleted === habit.id;
                  const group = habit.doneToday ? "habits.completed" : habit.dueToday ? "habits.forToday" : "habits.otherDays";
                  const previous = orderedHabits[i - 1];
                  const previousGroup = previous && (previous.doneToday ? "habits.completed" : previous.dueToday ? "habits.forToday" : "habits.otherDays");
                  return (<React.Fragment key={habit.id}>
                    {group !== previousGroup && <h2 className="text-xs font-semibold text-muted-foreground pt-3">{t(group)}</h2>}
                    <motion.div
                      key={habit.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(0.5, 0.04 + i * 0.03) }}
                      className="rounded-2xl relative overflow-hidden"
                      style={{
                        background: `linear-gradient(135deg, hsl(220 35% 10%), ${colors.bg})`,
                        borderTop: `1px solid ${habit.doneToday ? colors.border : "hsl(220 20% 18%)"}`,
                        borderRight: `1px solid ${habit.doneToday ? colors.border : "hsl(220 20% 18%)"}`,
                        borderBottom: `1px solid ${habit.doneToday ? colors.border : "hsl(220 20% 18%)"}`,
                        borderLeft: `3px solid ${habit.doneToday ? colors.text : colors.border}`,
                        boxShadow: habit.doneToday
                          ? `0 0 16px ${colors.glow}, inset 0 0 20px ${colors.bg}`
                          : isJustDone
                          ? `0 0 20px ${colors.glow}`
                          : `inset 3px 0 12px -6px ${colors.glow}`,
                        transition: "all 0.3s ease",
                        minHeight: 76,
                      }}
                    >
                      {isJustDone && (
                        <motion.div
                          className="absolute inset-0 pointer-events-none"
                          initial={{ opacity: 0.6 }}
                          animate={{ opacity: 0 }}
                          transition={{ duration: 0.8 }}
                          style={{ background: `radial-gradient(ellipse at center, ${colors.glow}, transparent 70%)` }}
                        />
                      )}

                      <div className="flex items-center gap-3 p-4">
                        {/* Small emoji badge */}
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-[14px] shrink-0"
                          style={{ background: colors.bg, border: `1px solid ${colors.border}` }}>
                          {habit.emoji}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-display text-[14px] font-bold leading-snug truncate text-white">
                              {habit.name}
                            </p>
                            <SourceBadge source={habit.source as Parameters<typeof SourceBadge>[0]["source"]} size="xs" />
                          </div>
                          <div className="flex items-center gap-3 mt-0.5">
                            <p className="text-[10px] font-body"
                              style={{ color: "hsl(218 14% 60%)" }}>
                              {t(`habits.freq.${habit.frequency}`)}
                            </p>
                            {(habit.streak ?? 0) > 0 && (
                              <span className="text-[10px] font-display font-medium flex items-center gap-1"
                                style={{ color: "hsl(25 95% 60%)" }}>
                                <Flame size={9} /> {habit.streak} {t("habits.streakDays")}
                              </span>
                            )}
                            {habit.streak === 0 && (
                              <span className="text-[10px] font-body" style={{ color: "hsl(0 55% 62%)" }}>
                                {t("habits.startStreak")}
                              </span>
                            )}
                          </div>
                        </div>

                        <button
                          disabled={saving || !snapshotForHabit(habit)} aria-label={t("habits.form.editTitle") + ": " + habit.name} onClick={() => { setFormError(undefined); setReviewChanged(false); setConfirmDeleteId(null); setEditingSnapshot(snapshotForHabit(habit)); setEditingHabit(habit); setView("edit"); }}
                          className="w-11 h-11 shrink-0 rounded-lg flex items-center justify-center opacity-40 hover:opacity-80 transition-opacity"
                        >
                          <Pencil size={12} className="text-white" />
                        </button>

                        {/* Completion circle with glow */}
                        <button type="button" aria-label={t(habit.doneToday ? "habits.undo" : "habits.complete") + ": " + habit.name} aria-pressed={habit.doneToday === true}
                          disabled={saving || !snapshotForHabit(habit) || (!habit.dueToday && !habit.doneToday)} onClick={() => void handleToggle(habit)} className="w-11 h-11 shrink-0 flex items-center justify-center disabled:opacity-40">
                          {saving ? <Loader2 className="animate-spin" size={20} /> : habit.doneToday ? (
                            <motion.div initial={{ scale: 0.6 }} animate={{ scale: 1 }}
                              transition={{ type: "spring", damping: 10, stiffness: 300 }}
                              style={{ filter: `drop-shadow(0 0 6px ${colors.glow})` }}>
                              <CheckCircle2 size={24} style={{ color: colors.text }} />
                            </motion.div>
                          ) : (
                            <Circle size={24} style={{ color: "hsl(210 20% 42%)" }} />
                          )}
                        </button>
                      </div>
                    </motion.div>
                  </React.Fragment>);
                })}
              </div>
            )}

            {/* ── Best streak card (cosmic/galactic) ──────────────────── */}
            {habits.every(habit => habit.completions_complete) && bestStreak && (bestStreak.streak ?? 0) > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="rounded-2xl p-4 relative overflow-hidden"
                style={{ background: "linear-gradient(145deg, #0a0a0f, hsl(220 35% 8%))" }}
              >
                {/* Rotating gradient border */}
                <div className="absolute inset-0 rounded-2xl pointer-events-none"
                  style={{ padding: 1, background: "linear-gradient(90deg, hsl(187 72% 50%), hsl(265 60% 60%), hsl(187 72% 50%), hsl(265 60% 60%), hsl(187 72% 50%))", backgroundSize: "300% 100%", animation: reducedMotion ? "none" : "rotatingBorder 8s linear infinite", WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)", WebkitMaskComposite: "xor", maskComposite: "exclude" }} />
                {/* Edge glows */}
                <div className="absolute inset-0 pointer-events-none rounded-2xl"
                  style={{ background: "radial-gradient(ellipse at 15% 20%, hsl(187 72% 50% / 0.08), transparent 55%), radial-gradient(ellipse at 85% 80%, hsl(265 60% 55% / 0.06), transparent 55%)" }} />
                {/* Star particles */}

                <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-3 relative z-10"
                  style={{ color: "hsl(187 72% 50% / 0.65)" }}>
                  {t("habits.bestStreak")}
                </p>
                <div className="flex items-center gap-3 mb-2.5 relative z-10">
                  <span className="text-[24px]">{bestStreak.emoji}</span>
                  <div>
                    <p className="font-display text-[15px] font-semibold text-foreground">{bestStreak.name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <Flame size={11} style={{ color: "hsl(187 72% 55%)" }} />
                      <p className="text-[11px] font-display font-medium" style={{ color: "hsl(187 72% 60%)" }}>
                        {bestStreak.streak} {t("habits.daysStraight")}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-2 pt-3 border-t border-white/5 relative z-10">
                  <Sparkles size={11} className="text-primary/50 mt-0.5 shrink-0" />
                  <p className="text-[12px] font-body leading-relaxed italic" style={{ color: "hsl(218 14% 60%)" }}>
                    {(bestStreak.streak ?? 0) >= 7
                      ? tf("habits.mirror.strong", { name: bestStreak.name, streak: String(bestStreak.streak) })
                      : (bestStreak.streak ?? 0) >= 3
                      ? tf("habits.mirror.building", { name: bestStreak.name, streak: String(bestStreak.streak) })
                      : tf("habits.mirror.starting", { name: bestStreak.name })}
                  </p>
                </div>
              </motion.div>
            )}

            {/* ── This Week dots (upgraded) ───────────────────────────── */}
            {habits.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                className="rounded-2xl p-4"
                style={{ background: "linear-gradient(135deg, hsl(220 35% 12%), hsl(225 30% 10%))", border: "1px solid hsl(187 72% 50% / 0.22)" }}>
                <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-3" style={{ color: "hsl(187 72% 50% / 0.65)" }}>
                  {t("habits.thisWeek")}
                </p>
                <div className="flex items-center justify-between gap-1 mb-2 relative">
                  {/* Connecting line */}
                  <div className="absolute top-[10px] left-[16px] right-[16px] h-[1px]"
                    style={{ borderTop: "1px dashed hsl(218 14% 25%)" }} />
                  {weekData.map((d, i) => {
                    const color = (d.rate ?? 0) >= 1 ? "hsl(142 71% 50%)" : (d.rate ?? 0) > 0 ? "hsl(187 72% 50%)" : "hsl(218 14% 25%)";
                    return (
                      <div key={i} aria-label={d.complete ? `${d.date}: ${d.done}/${d.total}` : t("habitAction.partial")} className="flex flex-col items-center gap-1.5 relative z-10">
                        <div className="w-5 h-5 rounded-full transition-all" style={{
                          background: (d.rate ?? 0) > 0 ? color : "hsl(220 35% 12%)",
                          border: `2px solid ${(d.rate ?? 0) > 0 ? color : "hsl(218 14% 30%)"}`,
                          boxShadow: (d.rate ?? 0) >= 1 ? `0 0 8px ${color}60` : d.isToday ? `0 0 6px hsl(187 72% 50% / 0.30)` : "none",
                          opacity: (d.rate ?? 0) >= 1 ? 1 : (d.rate ?? 0) > 0 ? 0.7 : 0.35,
                        }} />
                        <span className="text-[9px] font-display" style={{ color: d.isToday ? "hsl(187 72% 55%)" : "hsl(218 14% 58%)" }}>
                          {t(`habits.weekDay.${d.day}`)}
                        </span>
                        <span className="text-[10px] text-muted-foreground">{d.complete ? d.total ? `${d.done}/${d.total}` : "—" : "…"}</span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] font-body text-center mt-1" style={{ color: "hsl(218 14% 60%)" }}>
                  {t("habits.weekEvidence")}
                </p>
              </motion.div>
            )}

            {/* ── Mirror Insight (upgraded with Supabase bg + sparkle) ── */}
            {/* HIDDEN: fake Mirror Insight — re-wire with real Padrão B per audit 23/04 (memory #23) */}
            {false && (habits.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
                className="rounded-2xl p-4 relative overflow-hidden"
                style={{
                  background: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.70)), url(${MIRROR_INSIGHT_BG}) center/cover no-repeat`,
                  border: "1px solid hsl(187 72% 50% / 0.22)",
                }}>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0" style={{ background: "hsl(187 72% 50% / 0.12)", border: "1px solid hsl(187 72% 50% / 0.22)" }}>
                    <motion.div animate={{ opacity: [0.5, 1, 0.5], scale: [1, 1.2, 1] }}
                      transition={{ duration: 2, repeat: reducedMotion ? 0 : Infinity, ease: "easeInOut" }}>
                      <Sparkles size={14} style={{ color: "hsl(187 72% 55%)" }} />
                    </motion.div>
                  </div>
                  <div>
                    <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-1" style={{ color: "hsl(187 72% 50% / 0.65)" }}>
                      {t("habits.mirrorInsightTitle")}
                    </p>
                    <p className="text-[12px] font-body leading-relaxed mb-2" style={{ color: "hsl(218 14% 68%)" }}>
                      {t("habits.mirrorInsight")}
                    </p>
                    <button onClick={() => navigate("/mirror")} className="flex items-center gap-1 font-display text-[11px] font-medium py-2 -ml-1 pl-1 pr-3" style={{ color: "hsl(187 72% 55%)" }}>
                      <span>{t("habits.talkToMirror")}</span>
                      <ArrowRight size={11} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}

            {habits.length > 0 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
                className="flex justify-center pb-2">
                <button
                  onClick={openAdd} aria-label={t("habits.addHabit")} disabled={saving}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl font-display text-[12px] font-semibold tracking-wide transition-all duration-200 active:scale-95"
                  style={{ background: "transparent", border: "1px solid hsl(187 72% 50% / 0.28)", color: "hsl(187 72% 55%)" }}
                >
                  <Plus size={13} />
                  {t("habits.addHabit")}
                </button>
              </motion.div>
            )}
          </motion.div>
        )}

        {view === "add" && (
          <motion.div key="add" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="relative z-10">
            <HabitForm onSave={handleAdd} onCancel={() => { if (!saving) setView("list"); }} saving={saving} error={formError} locked={reviewChanged} t={t} />
          </motion.div>
        )}

        {view === "edit" && editingHabit && (
          <motion.div key="edit" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="relative z-10">
            <HabitForm initial={editingHabit} onSave={handleUpdate}
              onCancel={() => { if (!saving) { setView("list"); setEditingHabit(null); } }} saving={saving} error={formError} locked={reviewChanged} t={t} />
            <AnimatePresence mode="wait">
              {confirmDeleteId === editingHabit.id ? (
                <motion.div key="confirm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="mt-4 p-3 rounded-xl space-y-2" style={{ background: "hsl(0 50% 15% / 0.20)", border: "1px solid hsl(0 55% 50% / 0.25)" }}>
                  <p className="text-[12px] font-display font-medium text-center" style={{ color: "hsl(0 55% 65%)" }}>
                    {t("habits.deleteConfirm")}
                  </p>
                  <div className="flex gap-2">
                    <button disabled={saving} onClick={() => setConfirmDeleteId(null)}
                      className="flex-1 py-2.5 rounded-xl font-display text-[11px] font-semibold transition-all active:scale-95"
                      style={{ background: "hsl(220 35% 14%)", border: "1px solid hsl(220 20% 25%)", color: "hsl(218 14% 60%)" }}>
                      {t("habits.cancel")}
                    </button>
                    <button disabled={saving || reviewChanged} onClick={() => void handleDelete(editingHabit.id)}
                      className="flex-1 py-2.5 rounded-xl font-display text-[11px] font-semibold transition-all active:scale-95"
                      style={{ background: "hsl(0 55% 45% / 0.25)", border: "1px solid hsl(0 55% 50% / 0.35)", color: "hsl(0 55% 70%)" }}>
                      {t("habits.confirm")}
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.button key="delete" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  disabled={saving || reviewChanged} onClick={() => { setDeleteSnapshot(editingSnapshot ? structuredClone(editingSnapshot) : null); setConfirmDeleteId(editingHabit.id); }}
                  className="w-full mt-4 flex items-center justify-center gap-2 py-3 rounded-xl font-display text-[12px] text-red-400/60 border border-red-500/15 hover:border-red-500/30 transition-all active:scale-95"
                >
                  <Trash2 size={13} />
                  {t("habits.deleteHabit")}
                </motion.button>
              )}
            </AnimatePresence>
          </motion.div>
        )}

      </AnimatePresence>

    </MobileLayout>
  );
};

export default Habits;
